import type * as React from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";

import {
  ORG_DETAIL_TAB_IDS,
  OrgDetailShell,
  type OrgDetailTabId,
} from "@/features/admin/components/orgs/OrgDetailShell";

/**
 * Organization detail — full-screen modal route.
 *
 * Wire path: ``/admin/orgs/$orgId``. Sibling to ``orgs/index.tsx`` (list)
 * under ``_authed/_modal/admin``. The previous parent-style mount at
 * ``orgs.tsx`` swallowed navigation silently — see the comment in
 * ``orgs/index.tsx`` for the full history.
 *
 * URL contract
 * ------------
 * Active tab is persisted via ``?tab=<members|invites|...>`` so:
 *   - "Apri dettaglio · Members tab" deep-links work (e.g. from email,
 *     audit log, Linear ticket).
 *   - Browser back navigates LIST → DETAIL (overview) → DETAIL (members)
 *     in three discrete steps — no "atomic step" jump-back.
 *   - Refresh keeps the user on the same tab they were inspecting.
 *
 * ``validateSearch`` whitelists the known tab ids; an unknown ``?tab=``
 * value silently coerces to ``"overview"`` (forward-compat with future
 * tab additions and link-rot from older drafts).
 *
 * v1 reference: ``ando-frontend/app/admin-v2/components/pages/organizations/
 * OrganizationDetailPage.tsx`` (1668 LoC monolith). Per memory rule
 * "v2 rewrite authorized for monoliths >1500 LoC" the layout is split
 * into ``OrgDetailShell`` + 10 focused tab components.
 */

interface OrgDetailSearch {
  readonly tab: OrgDetailTabId;
}

export const Route = createFileRoute("/_authed/_modal/admin/orgs/$orgId")({
  component: OrgDetailRoute,
  validateSearch: (raw: Record<string, unknown>): OrgDetailSearch => {
    const candidate = typeof raw.tab === "string" ? raw.tab : "";
    const tab = (ORG_DETAIL_TAB_IDS as readonly string[]).includes(candidate)
      ? (candidate as OrgDetailTabId)
      : "overview";
    return { tab };
  },
});

function OrgDetailRoute(): React.ReactElement {
  const { orgId } = useParams({ from: "/_authed/_modal/admin/orgs/$orgId" });
  return <OrgDetailShell orgId={orgId} />;
}
